import { useEffect, useRef } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { getLandingHashForAnalytics } from '../../deep-link';
import { useAnalyticsEvents } from '../generated/use-analytics-events';
import type { MicrositeTier, AuthState } from '../types';

export type SessionTrackerProps = {
	authState: AuthState;
	/**
	 * Resolved tier for this page load, threaded through to
	 * `screen.twgMicrositeHome.viewed` so dashboards can group
	 * impressions by tier without joining to `track.twgMicrositeTier.shown`.
	 *
	 * Optional: callers in the logged-out path can pass
	 * `MicrositeTier.LoggedOut` directly; the logged-in path may pass
	 * `undefined` if the screen event must fire before tier resolves.
	 * The `track.twgMicrositeTier.shown` event remains the source of
	 * truth for tier mix; this attribute is a join-free convenience.
	 */
	tier?: MicrositeTier;
};

/**
 * Lifecycle-only component (renders nothing). Mounted by the home page in
 * each auth-state branch so it remounts on auth-state flips.
 *
 * Fires:
 *  - `screen.twgMicrositeHome.viewed` once on mount.
 *  - track.session.ended.twgMicrositeHome` exactly once per page-load
 *    on the first hidden signal (visibilitychange to 'hidden' OR pagehide),
 *    carrying durationMs since mount.
 */
/**
 * Returns a monotonic clock reading in milliseconds. Falls back to
 * `Date.now()` in environments where `performance.now()` is unavailable
 * (e.g. some SSR shims). `performance.now()` is preferred because it is
 * not affected by wall-clock skew (NTP corrections, manual clock changes)
 * and therefore cannot produce negative `durationMs` values that would
 * pollute the Time-on-Page / Avg Session Duration dashboards.
 */
const now = (): number =>
	typeof performance !== 'undefined' && typeof performance.now === 'function'
		? performance.now()
		: Date.now();

export const SessionTracker = ({ authState, tier }: SessionTrackerProps): null => {
	const { fireEvent } = useAnalyticsEvents();
	const mountTime = useRef(now());
	const sessionEndedFired = useRef(false);
	// Guards against StrictMode's intentional double-invocation of effects
	// in development (mount → cleanup → re-mount), which would otherwise
	// produce two `screen.twgMicrositeHome.viewed` events per real mount.
	const screenViewedFired = useRef(false);
	const authStateRef = useRef(authState);
	const tierRef = useRef(tier);

	useEffect(() => {
		if (screenViewedFired.current) {
			return;
		}
		screenViewedFired.current = true;
		fireEvent('screen.twgMicrositeHome.viewed', {
			authState: authStateRef.current,
			// `tier` is optional on the spec; only included when known
			// at first paint. The `loggedOut` branch always passes it
			// directly; the logged-in branch leaves it undefined so the
			// screen event fires immediately without waiting for metrics.
			...(tierRef.current !== undefined ? { tier: tierRef.current } : {}),
			// `landingHash` is null unless the page was deep-linked to a
			// known section (e.g. `#cli`).
			landingHash: getLandingHashForAnalytics(),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Bind visibility/pagehide listeners exactly once per mount. Same
	// reasoning as above — empty deps avoid re-binding on prop changes
	// while still firing the correct `authState` (read via the ref).
	useEffect(() => {
		const fire = (): void => {
			if (sessionEndedFired.current) {
				return;
			}
			sessionEndedFired.current = true;
			fireEvent('track.session.ended.twgMicrositeHome', {
				durationMs: now() - mountTime.current,
				authState: authStateRef.current,
			});
		};
		const doc = getDocument();
		if (!doc) {
			return undefined;
		}
		const onVisibility = (): void => {
			if (doc.visibilityState === 'hidden') {
				fire();
			}
		};
		// BFCache restore: when the user navigates away with `pagehide`
		// and later returns via the browser's back/forward cache, the JS
		// context is reused — including our `sessionEndedFired` flag and
		// the original `mountTime`. Without resetting, the next visit
		// would never emit `session.ended` (and `durationMs` would be
		// inflated by the time spent in BFCache). `pageshow` with
		// `event.persisted === true` signals a BFCache restore, so we
		// reset the per-session state to start a fresh session.
		const onPageShow = (event: Event): void => {
			if ((event as PageTransitionEvent).persisted) {
				sessionEndedFired.current = false;
				mountTime.current = now();
			}
		};
		const cleanups = [
			bind(doc, { type: 'visibilitychange', listener: onVisibility }),
			bind(window, { type: 'pagehide', listener: fire }),
			bind(window, { type: 'pageshow', listener: onPageShow }),
		];
		return () => cleanups.forEach((c) => c());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};
